<template>
  <div>
    <base-page-heading
      title="New Import"
      show-back-button
    />

    <div class="content">
      <b-tabs
        v-model="activeCategoryIndex"
        vertical
        nav-wrapper-class="flex-shrink-0"
        nav-class="nav flex-column border-0 py-2"
        content-class="py-4"
        class="import-tabs d-flex"
      >
        <b-tab
          v-for="(category, index) in categories"
          :key="index"
          no-body
          title-link-class="sidebar-link-appearance"
        >
          <template #title>
            <i :class="['fa fa-fw mr-2 text-muted', category.icon]" />
            <span class="text-nowrap text-truncate">{{ category.name }}</span>
          </template>

          <div class="py-0">
            <!-- Most Common Section -->
            <div
              v-if="index === 0"
            >
              <h5 class="text-muted mb-3">
                <i class="fa fa-star text-warning mr-2" /> Frequently Used
              </h5>
              <b-row>
                <b-col
                  lg="6"
                  class="mb-3"
                  v-for="action in commonActions"
                  :key="action.id"
                >
                  <import-action-card
                    :label="action.label"
                    :icon="action.icon || 'fa-upload'"
                    :subtitle="action.category"
                    @click="openModal(action.id)"
                  />
                </b-col>
              </b-row>
              <hr class="my-4">
            </div>

            <!-- Active Category Actions -->
            <h5 class="text-muted mb-3">
              <i :class="['fa mr-2', category.icon]" />
              {{ category.name }} Imports
            </h5>

            <b-row>
              <b-col
                lg="6"
                class="mb-3"
                v-for="action in categoryActions(index)"
                :key="action.id"
              >
                <import-action-card
                  :label="action.label"
                  :icon="action.icon || 'fa-arrow-right'"
                  @click="openModal(action.id)"
                />
              </b-col>
            </b-row>

            <div
              v-if="categoryActions(index).length === 0"
              class="text-center text-muted py-5"
            >
              <i class="fa fa-lock fa-3x mb-3 text-light" />
              <p>You do not have permission to perform any actions in this category.</p>
            </div>
          </div>
        </b-tab>
      </b-tabs>
    </div>

    <!-- Page Modals -->
    <work-orders-import-new-modal
      v-if="$can('create', 'Import')"
      @on-save="onSave"
    />
    <work-order-charges-import-new-modal
      v-if="$can('create', 'Import')"
      @on-save="onSave"
    />
    <update-work-order-charges-import-new-modal
      v-if="$can('create', 'Import')"
      @on-save="onSave"
    />
    <assign-work-orders-import-new-modal
      v-if="$can('create', 'Import')"
      @on-save="onSave"
    />
    <check-out-work-orders-import-new-modal
      v-if="$can('create', 'Import')"
      @on-save="onSave"
    />
    <cancel-work-orders-import-new-modal
      v-if="$can('create', 'Import')"
      @on-save="onSave"
    />
    <update-work-orders-import-new-modal
      v-if="$can('create', 'Import')"
      @on-save="onSave"
    />
    <reopen-work-orders-import-new-modal
      v-if="$can('create', 'Import')"
      @on-save="onSave"
    />
    <integrations-work-orders-import-new-modal
      v-if="$can('create', 'Import')"
      @on-save="onSave"
    />
    <officetrax-work-orders-photos-push-import-new-modal
      v-if="$can('create', 'Import')"
      @on-save="onSave"
    />
    <officetrax-work-orders-accepts-push-import-new-modal
      v-if="$can('create', 'Import')"
      @on-save="onSave"
    />
    <officetrax-work-orders-completions-push-import-new-modal
      v-if="$can('create', 'Import')"
      @on-save="onSave"
    />
    <service-channel-work-orders-photos-push-import-new-modal
      v-if="$can('create', 'Import')"
      @on-save="onSave"
    />
    <service-channel-work-orders-push-import-new-modal
      v-if="$can('create', 'Import')"
      @on-save="onSave"
    />
    <users-import-new-modal
      v-if="$can('create', 'Import')"
      @on-save="onSave"
    />
    <clients-import-new-modal
      v-if="$can('create', 'Import')"
      @on-save="onSave"
    />
    <locations-import-new-modal
      v-if="$can('create', 'Import')"
      @on-save="onSave"
    />
    <technician-services-import-new-modal
      v-if="$can('create', 'Import')"
      @on-save="onSave"
    />
    <technician-services-import-delete-modal
      v-if="$can('create', 'Import')"
      @on-save="onSave"
    />
    <technician-payment-items-import-new-modal
      v-if="$can('create_item', 'TechnicianPayment')"
      @on-save="onSave"
    />
    <technician-payment-items-from-work-order-import-new-modal
      v-if="$can('create_item', 'TechnicianPayment')"
      @on-save="onSave"
    />
    <invoice-items-import-new-modal
      v-if="$can('create', 'Import')"
      @on-save="onSave"
    />
    <recurring-charges-import-new-modal
      v-if="$can('create', 'Import')"
      @on-save="onSave"
    />
    <recurring-payments-import-new-modal
      v-if="$can('create', 'Import')"
      @on-save="onSave"
    />
    <recurring-work-orders-import-new-modal
      v-if="$can('create', 'Import')"
      @on-save="onSave"
    />
    <recurring-work-orders-import-delete-modal
      v-if="$can('create', 'Import')"
      @on-save="onSave"
    />
    <remove-work-orders-from-invoice-import-new-modal
      v-if="$can('destroy', 'InvoiceItem')"
      @on-save="onSave"
    />
    <remove-work-orders-from-technician-payment-import-new-modal
      v-if="$can('create_item', 'TechnicianPayment')"
      @on-save="onSave"
    />
    <tax-rates-import-new-modal
      v-if="$can('create', 'TaxRate')"
      @on-save="onSave"
    />
    <supplies-import-new-modal
      v-if="$can('create', 'Supply')"
      @on-save="onSave"
    />
    <delete-invoices-import-new-modal
      v-if="$can('destroy', 'Invoice')"
      @on-save="onSave"
    />
    <commissions-import-new-modal
      v-if="$can('create', 'Commission')"
      @on-save="onSave"
    />
  </div>
</template>

<script>
import WorkOrdersImportNewModal from '@/components/admin/imports/WorkOrdersNewModal.vue';
import TaxRatesImportNewModal from '@/components/admin/imports/TaxRatesNewModal.vue';
import SuppliesImportNewModal from '@/components/admin/imports/SuppliesNewModal.vue';
import WorkOrderChargesImportNewModal from '@/components/admin/imports/WorkOrderChargesNewModal.vue';
import AssignWorkOrdersImportNewModal from '@/components/admin/imports/AssignWorkOrdersNewModal.vue';
import CheckOutWorkOrdersImportNewModal from '@/components/admin/imports/CheckOutWorkOrdersNewModal.vue';
import CancelWorkOrdersImportNewModal from '@/components/admin/imports/CancelWorkOrdersNewModal.vue';
import UpdateWorkOrderChargesImportNewModal from '@/components/admin/imports/UpdateWorkOrderChargesNewModal.vue';
import UpdateWorkOrdersImportNewModal from '@/components/admin/imports/UpdateWorkOrdersNewModal.vue';
import ReopenWorkOrdersImportNewModal from '@/components/admin/imports/ReopenWorkOrdersNewModal.vue';
import IntegrationsWorkOrdersImportNewModal from '@/components/admin/imports/IntegrationsWorkOrdersNewModal.vue';
import OfficetraxWorkOrdersPhotosPushImportNewModal from '@/components/admin/imports/OfficetraxWorkOrdersPhotosPushNewModal.vue';
import OfficetraxWorkOrdersAcceptsPushImportNewModal from '@/components/admin/imports/OfficetraxWorkOrdersAcceptsPushNewModal.vue';
import OfficetraxWorkOrdersCompletionsPushImportNewModal from '@/components/admin/imports/OfficetraxWorkOrdersCompletionsPushNewModal.vue';
import ServiceChannelWorkOrdersPushImportNewModal from '@/components/admin/imports/ServiceChannelWorkOrdersPushNewModal.vue';
import ServiceChannelWorkOrdersPhotosPushImportNewModal from '@/components/admin/imports/ServiceChannelWorkOrdersPhotosPushNewModal.vue';
import TechnicianServicesImportNewModal from '@/components/admin/imports/TechnicianServicesNewModal.vue';
import TechnicianServicesImportDeleteModal from '@/components/admin/imports/TechnicianServicesDeleteModal.vue';
import TechnicianPaymentItemsImportNewModal from '@/components/admin/imports/TechnicianPaymentItemsNewModal.vue';
import TechnicianPaymentItemsFromWorkOrderImportNewModal from '@/components/admin/imports/TechnicianPaymentItemsFromWorkOrderNewModal.vue';
import InvoiceItemsImportNewModal from '@/components/admin/imports/InvoiceItemsNewModal.vue';
import RecurringChargesImportNewModal from '@/components/admin/imports/RecurringChargesNewModal.vue';
import RecurringPaymentsImportNewModal from '@/components/admin/imports/RecurringPaymentsNewModal.vue';
import RecurringWorkOrdersImportNewModal from '@/components/admin/imports/RecurringWorkOrdersNewModal.vue';
import RecurringWorkOrdersImportDeleteModal from '@/components/admin/imports/RecurringWorkOrdersDeleteModal.vue';
import UsersImportNewModal from '@/components/admin/imports/UsersNewModal.vue';
import ClientsImportNewModal from '@/components/admin/imports/ClientsNewModal.vue';
import LocationsImportNewModal from '@/components/admin/imports/LocationsNewModal.vue';
import RemoveWorkOrdersFromInvoiceImportNewModal from '@/components/admin/imports/RemoveWorkOrdersFromInvoiceNewModal.vue';
import RemoveWorkOrdersFromTechnicianPaymentImportNewModal from '@/components/admin/imports/RemoveWorkOrdersFromTechnicianPaymentNewModal.vue';
import DeleteInvoicesImportNewModal from '@/components/admin/imports/DeleteInvoicesNewModal.vue';
import CommissionsImportNewModal from '@/components/admin/imports/CommissionsNewModal.vue';
import ImportActionCard from '@/components/admin/imports/ImportActionCard.vue';

export default {
  name: 'ImportsNew',
  components: {
    ImportActionCard,
    TechnicianServicesImportNewModal,
    TechnicianServicesImportDeleteModal,
    TechnicianPaymentItemsImportNewModal,
    TechnicianPaymentItemsFromWorkOrderImportNewModal,
    InvoiceItemsImportNewModal,
    UsersImportNewModal,
    ClientsImportNewModal,
    WorkOrdersImportNewModal,
    WorkOrderChargesImportNewModal,
    AssignWorkOrdersImportNewModal,
    CheckOutWorkOrdersImportNewModal,
    CancelWorkOrdersImportNewModal,
    UpdateWorkOrderChargesImportNewModal,
    UpdateWorkOrdersImportNewModal,
    ReopenWorkOrdersImportNewModal,
    IntegrationsWorkOrdersImportNewModal,
    OfficetraxWorkOrdersPhotosPushImportNewModal,
    OfficetraxWorkOrdersAcceptsPushImportNewModal,
    OfficetraxWorkOrdersCompletionsPushImportNewModal,
    ServiceChannelWorkOrdersPushImportNewModal,
    ServiceChannelWorkOrdersPhotosPushImportNewModal,
    LocationsImportNewModal,
    RecurringChargesImportNewModal,
    RecurringPaymentsImportNewModal,
    RecurringWorkOrdersImportNewModal,
    RemoveWorkOrdersFromInvoiceImportNewModal,
    RemoveWorkOrdersFromTechnicianPaymentImportNewModal,
    TaxRatesImportNewModal,
    SuppliesImportNewModal,
    RecurringWorkOrdersImportDeleteModal,
    DeleteInvoicesImportNewModal,
    CommissionsImportNewModal,
  },
  data() {
    return {
      activeCategoryIndex: 0,
      allActions: [
        // Work Orders
        { id: 'work-orders-imports-add-modal', label: 'Create Work Orders', category: 'Work Orders', icon: 'fa-plus', isCommon: true },
        { id: 'update-work-orders-imports-add-modal', label: 'Update Work Orders', category: 'Work Orders', icon: 'fa-edit', isCommon: true },
        { id: 'cancel-work-orders-imports-add-modal', label: 'Cancel', category: 'Work Orders', icon: 'fa-ban' },
        { id: 'reopen-work-orders-imports-add-modal', label: 'Reopen Cancelled', category: 'Work Orders', icon: 'fa-folder-open' },
        { id: 'assign-work-orders-imports-add-modal', label: 'Assign', category: 'Work Orders', icon: 'fa-user-plus' },
        { id: 'check-out-work-orders-imports-add-modal', label: 'Check Out', category: 'Work Orders', icon: 'fa-calendar-plus', isCommon: true },
        { id: 'work-order-charges-imports-add-modal', label: 'Create Work Order Charges', category: 'Work Orders', icon: 'fa-plus' },
        { id: 'update-work-order-charges-imports-add-modal', label: 'Update Work Order Charges', category: 'Work Orders', icon: 'fa-edit' },

        // 3rd Party Integrations
        { id: 'integrations-work-orders-imports-add-modal', label: 'Edit Integration Data', category: '3rd Party Integrations', icon: 'fa-plug' },
        { id: 'officetrax-work-orders-accepts-push-imports-add-modal', label: 'Push Accepts to Officetrax', category: '3rd Party Integrations', icon: 'fa-check-double' },
        { id: 'officetrax-work-orders-photos-push-imports-add-modal', label: 'Push Photos to Officetrax', category: '3rd Party Integrations', icon: 'fa-sync' },
        { id: 'officetrax-work-orders-completions-push-imports-add-modal', label: 'Push Completions to Officetrax', category: '3rd Party Integrations', icon: 'fa-calendar-plus' },
        { id: 'service-channel-work-orders-photos-push-imports-add-modal', label: 'Push Photos to ServiceChannel', category: '3rd Party Integrations', icon: 'fa-sync' },
        { id: 'service-channel-work-orders-push-imports-add-modal', label: 'Push Completions to ServiceChannel', category: '3rd Party Integrations', icon: 'fa-calendar-plus' },

        // Client Management
        { id: 'clients-imports-add-modal', label: 'Manage Clients', category: 'Client Management', icon: 'fa-building' },
        { id: 'locations-imports-add-modal', label: 'Manage Locations', category: 'Client Management', icon: 'fa-map-marker' },
        { id: 'recurring-work-orders-imports-add-modal', label: 'Create Recurring Work Orders', category: 'Client Management', icon: 'fa-plus' },
        { id: 'recurring-work-orders-imports-delete-modal', label: 'Delete Recurring Work Orders', category: 'Client Management', icon: 'fa-trash' },

        // User Management
        { id: 'users-imports-add-modal', label: 'Manage Users', category: 'User Management', icon: 'fa-users' },
        { id: 'technician-services-imports-add-modal', label: 'New Technician Services', category: 'User Management', icon: 'fa-plus' },
        { id: 'technician-services-imports-delete-modal', label: 'Delete Technician Services', category: 'User Management', icon: 'fa-trash' },

        // Accounts Receivable
        { id: 'invoice-items-imports-add-modal', label: 'Create Invoice Items', category: 'Accounts Receivable', icon: 'fa-plus', permission: () => this.$can('create', 'InvoiceItem') },
        { id: 'remove-work-orders-from-invoice-imports-add-modal', label: 'Remove Work Order from Invoice', category: 'Accounts Receivable', icon: 'fa-eraser', isCommon: true, permission: () => this.$can('destroy', 'InvoiceItem') },
        { id: 'delete-invoices-imports-add-modal', label: 'Delete Invoices', category: 'Accounts Receivable', icon: 'fa-trash', permission: () => this.$can('destroy', 'Invoice') },
        { id: 'recurring-charges-imports-add-modal', label: 'New Recurring Charges', category: 'Accounts Receivable', icon: 'fa-refresh' },
        { id: 'tax-rates-imports-add-modal', label: 'Create Tax Rates', category: 'Accounts Receivable', icon: 'fa-calculator' },
        { id: 'supplies-imports-add-modal', label: 'Manage Supplies', category: 'Accounts Receivable', icon: 'fa-shopping-cart' },

        // Accounts Payable
        { id: 'technician-payment-items-imports-add-modal', label: 'Create Technician Payment Items', category: 'Accounts Payable', icon: 'fa-plus', permission: () => this.$can('create_item', 'TechnicianPayment') },
        { id: 'technician-payment-items-from-work-order-imports-add-modal', label: 'Create Payment Items From Work Orders', category: 'Accounts Payable', icon: 'fa-business-time', permission: () => this.$can('create_item', 'TechnicianPayment') },
        { id: 'remove-work-orders-from-technician-payment-imports-add-modal', label: 'Remove Work Order from Payment', category: 'Accounts Payable', icon: 'fa-eraser', permission: () => this.$can('create_item', 'TechnicianPayment') },
        { id: 'recurring-payments-imports-add-modal', label: 'Create Recurring Payments', category: 'Accounts Payable', icon: 'fa-refresh' },
        { id: 'commissions-imports-add-modal', label: 'Create Commissions', category: 'Accounts Payable', icon: 'fa-percent', permission: () => this.$can('create', 'Commission') },

      ],
      categories: [
        { name: 'Work Orders', icon: 'fa-business-time' },
        { name: 'Accounts Receivable', icon: 'fa-dollar' },
        { name: 'Accounts Payable', icon: 'fa-credit-card' },
        { name: 'Client Management', icon: 'fa-building' },
        { name: '3rd Party Integrations', icon: 'fa-plug' },
        { name: 'User Management', icon: 'fa-users' },
      ]
    };
  },
  computed: {
    permittedActions() {
      return this.allActions.filter(action => {
        if (action.permission) {
          return action.permission();
        }
        return true;
      });
    },
    commonActions() {
      return this.permittedActions.filter(action => action.isCommon);
    }
  },
  methods: {
    categoryActions(index) {
      const category = this.categories[index];
      return category ? this.permittedActions.filter(action => action.category === category.name) : [];
    },
    openModal(modalId) {
      this.$bvModal.show(modalId);
    },
    onSave() {
      // After import starts/completes, navigate back to list to see the record
      this.$router.push({ name: 'imports' });
    }
  }
};
</script>

<style scoped>
.import-tabs >>> .nav {
  width: 280px;
  min-width: 280px;
  border: none;
}
.import-tabs >>> .nav-item {
  padding-bottom: 0.75rem;
}
.import-tabs >>> .nav-link {
  border: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
